/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

const moment = require('moment');
require('moment-range');

export function startOfMonth(date: Date): Date {
  return moment(date).startOf('month').toDate();
}

export function endOfMonth(date: Date): Date {
  return moment(date).endOf('month').toDate();
}

export function startOfWeek(date: Date, weekStartsOn: number = 0): Date {
  return moment(date).day() === weekStartsOn ?
    moment(date).startOf('day').toDate() :
    moment(date).day(weekStartsOn).startOf('day').toDate();
}

export function endOfWeek(date: Date, weekEndsOn: number = 6): Date {
  return moment(date).day() === weekEndsOn ?
    moment(date).endOf('day').toDate() :
    moment(date).day(weekEndsOn).endOf('day').toDate();
}

export function startOfWeekOn(date: Date, weekStartsOn: number = 0): Date {
  return moment(date).startOf('week').day(weekStartsOn).toDate();
}

export function endOfWeekOn(date: Date, weekStartsOn: number = 0): Date {
  return moment(date).endOf('isoweek').toDate();
}

export function addYears(date: Date, amount: number = 1): Date {
  return moment(date).add(amount, 'years').toDate();
}

export function addMonths(date: Date, amount: number = 1): Date {
  return moment(date).add(amount, 'months').toDate();
}

export function addWeeks(date: Date, amount: number = 1): Date {
  return moment(date).add(amount, 'weeks').toDate();
}

export function subYears(date: Date, amount: number = 1): Date {
  return moment(date).subtract(amount, 'years').toDate();
}

export function subMonths(date: Date, amount: number = 1): Date {
  return moment(date).subtract(amount, 'months').toDate();
}

export function subWeeks(date: Date, amount: number = 1): Date {
  return moment(date).subtract(amount, 'weeks').toDate();
}

export function startOfDay(date: Date): Date {
  return moment(date).startOf('day').toDate();
}

export function endOfDay(date: Date, keepLocalTime: boolean = false): Date {
  return moment(date).endOf('day').utc(keepLocalTime).toDate();
}

export function addDays(date: Date, amount: number = 1): Date {
  return moment(date).add(amount, 'days').toDate();
}

export function differenceInDays(dateLeft: Date, dateRight: Date): number {
  return moment(dateLeft).diff(moment(dateRight), 'days');
}

export function isSameDay(dateLeft: Date, dateRight: Date): boolean {
  return moment(dateLeft).isSame(dateRight, 'day');
}

export function isSameMonth(dateLeft: Date, dateRight: Date): boolean {
  return moment(dateLeft).isSame(dateRight, 'month');
}

export function isSameYear(dateLeft: Date, dateRight: Date): boolean {
  return moment(dateLeft).isSame(dateRight, 'year');
}

export function isSameSecond(dateLeft: Date, dateRight: Date): boolean {
  return moment(dateLeft).isSame(dateRight, 'second');
}

export function getDay(date: Date): number {
  return moment(date).day();
}

export function format(date: Date, format: string): string {
  return moment(date.getTime()).format(format);
}

export function isValid(date: Date | string, format?: string): boolean {
  if (date instanceof Date) {
    return moment(date).isValid();
  }
  if (typeof date === 'string') {
    return moment(date, format, true).isValid();
  }
  return false;
}

export function strToDate(value: string, format?: string): Date {
  return format !== null ?
    moment(value, format, true).toDate() :
    moment(value).toDate();
}

export function setMonth(currentDate: Date, month: number): Date {
  return moment(currentDate)
    .set('month', month)
    .toDate();
}

export function setDay(currentDate: Date, day: number): Date {
  return moment(currentDate)
    .set('date', day)
    .toDate();
}

export function range(startDate: Date | string, endDate: Date | string): any {
  return moment.range(startDate, endDate);
}

export function timeAgo(value: string) {
  return moment.utc(value).fromNow();
}

export function timeLeft(value: string) {
  return moment.utc(value).toNow();
}

export function getDefaultLocalTimezone(): string {
  const localMoment = moment();
  localMoment.locale('en');
  const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess();
  const tz = localMoment.tz(defaultTimezone.toString()).format('Z');
  const offset = parseInt(tz, 10);

  return offset < 0 ? ('Etc/GMT+' + -offset) : ('Etc/GMT' + -offset);
}

export const isBeforeToday = (dueDate: string) => {
  return moment(dueDate).isBefore(new Date());
};
